// 기억의 만찬 (Memory Feast) engine types.
// Rules: docs/wireframes/memory-feast/rule.md (2026-06-18 verified).
// 20 covered bowls; placement rounds 1..9 (place N distinct bowls, +1 each) +
// bonus 10; combined per-bowl count is judged; open 2 bowls — equal count wins.
// Hidden information: bowl counts are server-authoritative (masked when covered).

export const MEMORY_FEAST_BOWL_COUNT = 20;
export const MEMORY_FEAST_PLACEMENT_ROUNDS = 9;
export const MEMORY_FEAST_BONUS_TOKENS = 10;
export const MEMORY_FEAST_PLAYERS = ["p1", "p2"] as const;

export type MemoryFeastPlayerId = (typeof MEMORY_FEAST_PLAYERS)[number];
export type MemoryFeastPhase = "place" | "place-bonus" | "open" | "game-over";

export interface ValidationResult {
  readonly valid: boolean;
  readonly error?: string;
  readonly details?: Record<string, unknown>;
}

export interface MemoryFeastBowl {
  /** 0-indexed id; display label is id + 1. */
  readonly id: number;
  /** Authoritative combined token count (both players). */
  readonly count: number;
  /** Whether the bowl is currently revealed (this open turn / winning pair). */
  readonly open: boolean;
}

export interface MemoryFeastPlayer {
  readonly playerId: MemoryFeastPlayerId;
  readonly label: string;
}

export interface PlaceTokensAction {
  readonly type: "PlaceTokens";
  readonly playerId: MemoryFeastPlayerId;
  /** Distinct bowl ids; length must equal this round's token budget; +1 each. */
  readonly bowlIds: readonly number[];
}

export interface OpenBowlsAction {
  readonly type: "OpenBowls";
  readonly playerId: MemoryFeastPlayerId;
  /** Exactly two distinct bowl ids to open. */
  readonly bowlIds: readonly number[];
}

export type MemoryFeastAction = PlaceTokensAction | OpenBowlsAction;

export interface MemoryFeastLastOpen {
  readonly playerId: MemoryFeastPlayerId;
  readonly bowlIds: readonly number[];
  /** Revealed counts for the opened bowls (public once opened). */
  readonly counts: readonly number[];
  readonly matched: boolean;
}

export interface MemoryFeastState {
  readonly gameId: string;
  readonly players: readonly MemoryFeastPlayer[];
  readonly phase: MemoryFeastPhase;
  readonly bowls: readonly MemoryFeastBowl[];
  /** Current placement round (1..9). Held at 9 during the bonus phase. */
  readonly round: number;
  readonly firstPlayerId: MemoryFeastPlayerId;
  readonly currentPlayerId: MemoryFeastPlayerId;
  readonly moveNumber: number;
  readonly lastOpen: MemoryFeastLastOpen | null;
  readonly winnerId: MemoryFeastPlayerId | null;
  readonly lastAction: MemoryFeastAction | null;
}

export interface InitGameOptions {
  readonly gameId?: string;
  readonly firstPlayerId?: MemoryFeastPlayerId;
}

// Viewer projection: covered bowls have their count masked to null.
export interface MemoryFeastViewerBowl {
  readonly id: number;
  readonly count: number | null;
  readonly open: boolean;
}

export interface MemoryFeastViewerState
  extends Omit<MemoryFeastState, "bowls"> {
  readonly bowls: readonly MemoryFeastViewerBowl[];
}
